import {
  Calendar,
  Facebook,
  Images,
  Instagram,
  Link as LinkIcon,
  Linkedin,
  MapPin,
  MessageSquare,
  Phone,
  Star,
  Twitter,
} from "lucide-react";
import { formatPhone, phoneHref, primaryMarketLabel, showsReviewCount } from "@servicepro/utils";
import type { BaseComponentProps } from '../../types/puck';
import type { JobsiteBusinessContext } from '../../types/runtime-config';
import React from "react";
import { cn } from "../../lib/utils";

/**
 * The link-in-bio page: one centered column of big tappable actions, served
 * live as the built-in "links" form on the booking host (SP-310,
 * wiki/decisions/link-page-form.md) and edited in the form builder.
 *
 * Rows are declared by kind, not by URL. Each auto kind resolves its target
 * from live business metadata at render time, so an untouched page stays
 * current as the business's data changes — and a row whose data is missing
 * (no GBP placeId yet, no gallery page) renders nothing on the live site
 * rather than a dead link. Only `custom` rows carry their own URL.
 */

const LINKS_PAGE_LINK_KINDS = [
    "call",
    "book",
    "quote",
    "review",
    "directions",
    "gallery",
    "custom",
  ] as const,
  /** Utm_source carried on cross-property links (the booking app), matched by a
   *  branch in packages/attribution's classifyChannel. Keep the value stable —
   *  it becomes a channel label in every analytics dataset that stores it. */
  LINKS_PAGE_UTM_SOURCE = "links_page";
const withUtmSource = (url: string, source: string): string => `${url}${url.includes("?") ? "&" : "?"}utm_source=${source}`,
  /**
   * Whether an internal page exists. The editor preview has no pagePaths, so it
   * optimistically shows the row there; the static build knows and prunes.
   */
  hasPage = (pagePaths: string[] | undefined, path: string): boolean => {
    if (!pagePaths) {
      return true;
    }
    return pagePaths.some((p) => p === path || p === `${path}/`);
  },
  /** Default button text per kind. Exported so page seeding (page-presets) and
   *  the component agree on the words the pro sees in their Label fields —
   *  labels are seeded as real values, not resolved invisibly at render, so the
   *  editor shows exactly what's on the button. The call row appends the live
   *  phone number at render, which is the one part that must stay current. */
  LINKS_PAGE_DEFAULT_LABELS: Record<LinksPageLinkKind, string> = {
    book: "Service request",
    call: "Call",
    custom: "Link",
    directions: "Get directions",
    gallery: "See our work",
    quote: "Get a quote",
    review: "Leave us a review",
  },
  ROW_ACTIONS: Record<LinksPageLinkKind, string> = {
    book: "book",
    call: "call",
    custom: "link",
    directions: "directions",
    gallery: "gallery",
    quote: "quote",
    review: "review",
  },
  isExternalHref = (href: string): boolean => /^https?:\/\//i.test(href),
  resolveLinksPageRows = (
    links: LinksPageLink[],
    business: JobsiteBusinessContext | undefined,
    pagePaths: string[] | undefined,
    siteBaseUrl?: string,
  ): { rows: ResolvedLinkRow[]; missing: MissingLinkRow[] } => {
    const missing: MissingLinkRow[] = [],
      rows: ResolvedLinkRow[] = [],
      sitePath = (path: string) => {
        if (siteBaseUrl) {
          return `${siteBaseUrl.replace(/\/+$/u, "")}${path}`;
        }
        return path;
      };

    links.forEach((link, index) => {
      const key = link.id || `${link.kind}-${index}`,
        label = link.label?.trim(),

        // An owner-set URL wins for every kind, skipping the automatic
        // Resolution AND its data preconditions — they chose the destination.
        override = link.url?.trim();
      if (override && link.kind !== "custom") {
        rows.push({
          action: ROW_ACTIONS[link.kind],
          external: isExternalHref(override),
          href: override,
          key,
          kind: link.kind,
          label: label || LINKS_PAGE_DEFAULT_LABELS[link.kind],
        });
        return;
      }

      switch (link.kind) {
        case "call": {
          const phone = business?.phone?.trim();
          if (!phone) {
            missing.push({ key, kind: link.kind, reason: "Appears once the business has a phone number." });
            return;
          }
          rows.push({
            key,
            kind: link.kind,
            href: phoneHref(phone),
            // The number IS the destination, so it stays on the button whatever
            // The label says — and stays current as the tracking number swaps.
            label: `${label || LINKS_PAGE_DEFAULT_LABELS.call} ${formatPhone(phone)}`,
            action: "call",
            external: false,
          });
          return;
        }
        case "book": {
          const bookingUrl = business?.bookingUrl?.trim();
          if (!bookingUrl || business?.bookingEnabled === false) {
            missing.push({ key, kind: link.kind, reason: "Appears while online booking is turned on." });
            return;
          }
          rows.push({
            action: "book",
            external: true,
            href: withUtmSource(bookingUrl, LINKS_PAGE_UTM_SOURCE),
            key,
            kind: link.kind,
            label: label || LINKS_PAGE_DEFAULT_LABELS.book,
          });
          return;
        }
        case "quote": {
          if (!hasPage(pagePaths, "/contact")) {
            missing.push({ key, kind: link.kind, reason: "Appears once the site has a contact page." });
            return;
          }
          rows.push({
            action: "quote",
            external: false,
            href: sitePath("/contact"),
            key,
            kind: link.kind,
            label: label || LINKS_PAGE_DEFAULT_LABELS.quote,
          });
          return;
        }
        case "review": {
          const placeId = business?.placeId?.trim();
          if (!placeId) {
            missing.push({ key, kind: link.kind, reason: "Appears once the Google Business Profile is connected." });
            return;
          }
          rows.push({
            action: "review",
            external: true,
            href: `https://search.google.com/local/writereview?placeid=${placeId}`,
            key,
            kind: link.kind,
            label: label || LINKS_PAGE_DEFAULT_LABELS.review,
          });
          return;
        }
        case "directions": {
          const destination = business?.formattedAddress?.trim();
          if (!destination) {
            missing.push({ key, kind: link.kind, reason: "Appears once the business has an address." });
            return;
          }
          const placeIdParam = business?.placeId !== undefined && business.placeId !== null && business.placeId !== ""
            ? `&destination_place_id=${business.placeId}`
            : "";
          rows.push({
            action: "directions",
            external: true,
            href: `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}${placeIdParam}`,
            key,
            kind: link.kind,
            label: label || LINKS_PAGE_DEFAULT_LABELS.directions,
          });
          return;
        }
        case "gallery": {
          if (!hasPage(pagePaths, "/gallery")) {
            missing.push({ key, kind: link.kind, reason: "Appears once the site has a gallery page." });
            return;
          }
          rows.push({
            action: "gallery",
            external: false,
            href: sitePath("/gallery"),
            key,
            kind: link.kind,
            label: label || LINKS_PAGE_DEFAULT_LABELS.gallery,
          });
          return;
        }
        case "custom": {
          const url = link.url?.trim();
          if (!url) {
            missing.push({ key, kind: link.kind, reason: "Add a URL to show this link." });
            return;
          }
          rows.push({
            action: "link",
            external: true,
            href: url,
            key,
            kind: link.kind,
            label: label || url.replace(/^https?:\/\//u, ""),
          });
          return;
        }
      }
    });

    return { missing, rows };
  },
  ROW_ICONS: Record<LinksPageLinkKind, React.ComponentType<{ className?: string }>> = {
    book: Calendar,
    call: Phone,
    custom: LinkIcon,
    directions: MapPin,
    gallery: Images,
    quote: MessageSquare,
    review: Star,
  },
  SOCIAL_NETWORKS = [
    { Icon: Facebook, key: "facebookUrl", label: "Facebook" },
    { Icon: Instagram, key: "instagramUrl", label: "Instagram" },
    { Icon: Linkedin, key: "linkedinUrl", label: "LinkedIn" },
    { Icon: Twitter, key: "twitterUrl", label: "X (Twitter)" },
  ] as const,
  noLinks: LinksPageLink[] = [],
  noPhotos: LinksPagePhoto[] = [],
  LinksPage: React.FC<LinksPageProps> = ({
    id,
    className,
    logoUrl,
    tagline,
    links = noLinks,
    photos = noPhotos,
    showRating = "yes",
    showSocials = "yes",
    business,
    pagePaths,
    siteBaseUrl,
    isEditing,
  }) => {
    const { rows, missing } = resolveLinksPageRows(links, business, pagePaths, siteBaseUrl),
      // In an editor surface a tap is a selection, not a navigation — a tel: or
      // Booking href firing mid-edit would yank the pro out of their work.
      guardEditorClick = isEditing === true
        ? (event: React.MouseEvent) => {
            event.preventDefault();
          }
        : undefined,
      visiblePhotos = photos.filter((photo) => Boolean(photo.imageUrl?.trim())),
      emptyPhotoCount = photos.length - visiblePhotos.length,

      name = business?.name?.trim(),
      // An explicit logo wins; otherwise the business's stored logo keeps the
      // Default (never-edited) page from rendering a bare monogram.
      resolvedLogoUrl = logoUrl?.trim() || business?.logoUrl?.trim() || "",
      resolvedTagline = tagline?.trim() || primaryMarketLabel(business?.primaryMarket),

      rating = business?.googleRating,
      reviewCount = business?.googleReviewCount,
      showRatingBadge = showRating === "yes" && typeof rating === "number" && rating > 0,

      socials =
        showSocials === "yes"
          ? SOCIAL_NETWORKS.flatMap(({ key, label, Icon }) => {
              const value = business?.[key];
              if (typeof value !== "string" || !value.trim()) {
                return [];
              }
              return [{ Icon, href: value.trim(), key, label }];
            })
          : [];

    return (
      <section
        id={id}
        aria-label="Links"
        className={cn("jobsite:bg-background jobsite:text-foreground", className)}
      >
        <div className="jobsite:mx-auto jobsite:flex jobsite:w-full jobsite:max-w-md jobsite:flex-col jobsite:items-center jobsite:gap-6 jobsite:px-5 jobsite:py-12">
          <header className="jobsite:flex jobsite:flex-col jobsite:items-center jobsite:gap-3 jobsite:text-center">
            {resolvedLogoUrl ? (
              <img
                src={resolvedLogoUrl}
                alt={name ? `${name} logo` : "Business logo"}
                className="jobsite:h-20 jobsite:w-20 jobsite:object-contain"
                style={{ borderRadius: "var(--jobsite-radius)" }}
              />
            ) : (name ? (
              <div
                aria-hidden="true"
                className="jobsite:flex jobsite:h-20 jobsite:w-20 jobsite:items-center jobsite:justify-center jobsite:bg-primary jobsite:font-heading jobsite:text-3xl jobsite:font-bold jobsite:text-primary-foreground"
                style={{ borderRadius: "var(--jobsite-radius)" }}
              >
                {name.charAt(0)}
              </div>
            ) : null)}
            {name && (
              <h1 className="jobsite:m-0 jobsite:font-heading jobsite:text-2xl jobsite:font-bold">
                {name}
              </h1>
            )}
            {resolvedTagline && (
              <p className="jobsite:m-0 jobsite:text-sm jobsite:text-muted-foreground">
                {resolvedTagline}
              </p>
            )}
            {showRatingBadge && (
              <p className="jobsite:m-0 jobsite:inline-flex jobsite:items-center jobsite:gap-1.5 jobsite:text-sm jobsite:font-medium">
                <Star
                  aria-hidden="true"
                  className="jobsite:h-4 jobsite:w-4 jobsite:fill-current jobsite:text-primary"
                />
                {rating.toFixed(1)}
                {typeof reviewCount === "number" && showsReviewCount(reviewCount) && (
                  <span className="jobsite:font-normal jobsite:text-muted-foreground">
                    · {reviewCount} Google reviews
                  </span>
                )}
              </p>
            )}
          </header>

          <nav aria-label="Business links" className="jobsite:flex jobsite:w-full jobsite:flex-col jobsite:gap-3">
            {rows.map((row) => {
              const Icon = ROW_ICONS[row.kind],
                solid = row.kind === "call";
              return (
                <a
                  key={row.key}
                  href={row.href}
                  onClick={guardEditorClick}
                  data-track={row.action}
                  data-track-label="links-page"
                  {...(row.external ? { rel: "noopener", target: "_blank" } : {})}
                  className={cn(
                    "jobsite:flex jobsite:min-h-14 jobsite:w-full jobsite:items-center jobsite:justify-center jobsite:gap-3 jobsite:px-5 jobsite:py-3.5 jobsite:text-center jobsite:font-semibold jobsite:transition-opacity jobsite:hover:opacity-85",
                    solid
                      ? "jobsite:bg-primary jobsite:text-primary-foreground"
                      : "jobsite:border jobsite:border-border jobsite:bg-card jobsite:text-card-foreground",
                  )}
                  style={{ borderRadius: "var(--jobsite-radius)" }}
                >
                  <Icon aria-hidden="true" className="jobsite:h-5 jobsite:w-5 jobsite:shrink-0" />
                  {row.label}
                </a>
              );
            })}

            {isEditing &&
              missing.map((row) => (
                <div
                  key={row.key}
                  className="jobsite:flex jobsite:w-full jobsite:items-center jobsite:justify-center jobsite:border jobsite:border-dashed jobsite:border-border jobsite:bg-muted/40 jobsite:px-5 jobsite:py-3 jobsite:text-center jobsite:text-sm jobsite:text-muted-foreground"
                  style={{ borderRadius: "var(--jobsite-radius)" }}
                >
                  {row.reason} Or set a URL on this row to link it anywhere now.
                </div>
              ))}
          </nav>

          {visiblePhotos.length > 0 && (
            <div className="jobsite:grid jobsite:w-full jobsite:grid-cols-3 jobsite:gap-2">
              {visiblePhotos.map((photo, index) => {
                const href = photo.url?.trim(),
                  caption = photo.caption?.trim(),
                  image = (
                    <img
                      src={photo.imageUrl}
                      alt={caption || "Photo"}
                      loading="lazy"
                      className="jobsite:aspect-square jobsite:w-full jobsite:object-cover"
                      style={{ borderRadius: "var(--jobsite-radius)" }}
                    />
                  ),
                  body = caption ? (
                    <figure className="jobsite:m-0 jobsite:flex jobsite:flex-col jobsite:gap-1">
                      {image}
                      <figcaption className="jobsite:truncate jobsite:text-center jobsite:text-xs jobsite:text-muted-foreground">
                        {caption}
                      </figcaption>
                    </figure>
                  ) : (
                    image
                  ),
                  key = photo.id || `photo-${index}`;
                if (!href) {
                  return <React.Fragment key={key}>{body}</React.Fragment>;
                }
                return (
                  <a
                    key={key}
                    href={href}
                    onClick={guardEditorClick}
                    data-track="link"
                    data-track-label="links-page"
                    {...(isExternalHref(href) ? { rel: "noopener", target: "_blank" } : {})}
                    className="jobsite:block jobsite:transition-opacity jobsite:hover:opacity-85"
                  >
                    {body}
                  </a>
                );
              })}
            </div>
          )}

          {isEditing && emptyPhotoCount > 0 && (
            <div
              className="jobsite:flex jobsite:w-full jobsite:items-center jobsite:justify-center jobsite:border jobsite:border-dashed jobsite:border-border jobsite:bg-muted/40 jobsite:px-5 jobsite:py-3 jobsite:text-center jobsite:text-sm jobsite:text-muted-foreground"
              style={{ borderRadius: "var(--jobsite-radius)" }}
            >
              {emptyPhotoCount === 1
                ? "A photo row is waiting for its picture."
                : `${emptyPhotoCount} photo rows are waiting for their pictures.`}
            </div>
          )}

          {socials.length > 0 && (
            <nav aria-label="Social profiles" className="jobsite:flex jobsite:items-center jobsite:gap-3">
              {socials.map(({ key, label, Icon, href }) => (
                <a
                  key={key}
                  href={href}
                  onClick={guardEditorClick}
                  target="_blank"
                  rel="noopener"
                  aria-label={label}
                  data-track="social"
                  data-track-label="links-page"
                  className="jobsite:flex jobsite:h-11 jobsite:w-11 jobsite:items-center jobsite:justify-center jobsite:rounded-full jobsite:border jobsite:border-border jobsite:bg-card jobsite:text-card-foreground jobsite:transition-opacity jobsite:hover:opacity-85"
                >
                  <Icon className="jobsite:h-5 jobsite:w-5" />
                </a>
              ))}
            </nav>
          )}
        </div>
      </section>
    );
  };

type LinksPageLinkKind = (typeof LINKS_PAGE_LINK_KINDS)[number];

interface LinksPageLink {
  id?: string;
  kind: LinksPageLinkKind;
  /** Blank means "use the kind's default label". */
  label?: string;
  /** Blank means "resolve automatically from business data" (auto kinds).
   *  Set, it wins for every kind — the owner's URL beats the derived one.
   *  Custom rows have no automatic fallback, so theirs is required. */
  url?: string;
}

interface LinksPageProps extends BaseComponentProps {
  /** Baked at page creation from the site's uploaded/scraped logo; editable. */
  logoUrl?: string;
  /** Blank derives "{city}, {state}" when the business has one. */
  tagline?: string;
  links?: LinksPageLink[];
  /** Photo grid under the link rows. Each photo optionally links somewhere. */
  photos?: LinksPagePhoto[];
  showRating?: "yes" | "no";
  showSocials?: "yes" | "no";
  /** Resolved by the Puck config from puck.metadata — never set as data. */
  business?: JobsiteBusinessContext;
  /** Real page paths, present in the static build; absent in the editor. */
  pagePaths?: string[];
  /** Absolute jobsite origin, set when the page renders OFF the jobsite (the
   *  go.servicepro.co form). Internal rows (quote, gallery) resolve against
   *  it so they land on the site instead of 404ing on the serving host. */
  siteBaseUrl?: string;
  isEditing?: boolean;
}

interface LinksPagePhoto {
  id?: string;
  imageUrl?: string;
  /** Short line under the photo. */
  caption?: string;
  /** Where tapping the photo goes; blank means the photo is just a photo. */
  url?: string;
}

interface ResolvedLinkRow {
  key: string;
  kind: LinksPageLinkKind;
  href: string;
  label: string;
  /** Sent as data-track — must stay within the tracker's [a-z_]+ vocabulary. */
  action: string;
  external: boolean;
}

/** Why a row rendered nothing — surfaced only as an editor placeholder. */
interface MissingLinkRow {
  key: string;
  kind: LinksPageLinkKind;
  reason: string;
}

export type { LinksPageLinkKind, LinksPageLink, LinksPageProps, LinksPagePhoto };
export {
  LINKS_PAGE_LINK_KINDS,
  LINKS_PAGE_UTM_SOURCE,
  withUtmSource,
  LINKS_PAGE_DEFAULT_LABELS,
  resolveLinksPageRows,
  LinksPage,
};
